/**
 * Transactions Tab Component
 *
 * Displays a filterable list of BUY/SELL transactions for a holding:
 * - Transaction date
 * - Transaction type (BUY/SELL badge)
 * - Units traded
 * - NAV at transaction
 * - Total amount
 */

import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatDate } from "@/lib/utils";
import { ArrowDownLeft, ArrowUpRight, Filter } from "lucide-react";
import type { TransactionType, HoldingId } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Filter options for transaction list
 */
export type TransactionFilter = "all" | "buy" | "sell";

/**
 * Transaction display data
 */
export interface TransactionDisplayData {
  id: string;
  date: string;
  type: TransactionType;
  units: number;
  nav: number;
  amount: number;
}

/**
 * Props for the TransactionsTab component
 */
export interface TransactionsTabProps {
  /** Holding ID for context */
  holdingId: HoldingId;
  /** List of transactions */
  transactions: TransactionDisplayData[];
  /** Loading state */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Transaction type configuration with colors and labels
 */
const TRANSACTION_CONFIG: Record<
  TransactionType,
  {
    label: string;
    category: "buy" | "sell" | "other";
    bgColor: string;
    textColor: string;
    icon: typeof ArrowDownLeft;
  }
> = {
  purchase: {
    label: "BUY",
    category: "buy",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    textColor: "text-emerald-700 dark:text-emerald-400",
    icon: ArrowDownLeft,
  },
  "switch-in": {
    label: "SWITCH IN",
    category: "buy",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    textColor: "text-emerald-700 dark:text-emerald-400",
    icon: ArrowDownLeft,
  },
  redemption: {
    label: "SELL",
    category: "sell",
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
    textColor: "text-rose-700 dark:text-rose-400",
    icon: ArrowUpRight,
  },
  "switch-out": {
    label: "SWITCH OUT",
    category: "sell",
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
    textColor: "text-rose-700 dark:text-rose-400",
    icon: ArrowUpRight,
  },
  dividend: {
    label: "DIVIDEND",
    category: "other",
    bgColor: "bg-sky-100 dark:bg-sky-900/30",
    textColor: "text-sky-700 dark:text-sky-400",
    icon: ArrowDownLeft,
  },
};

/**
 * Filter button configuration
 */
const FILTER_OPTIONS: { value: TransactionFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a transaction matches the current filter
 */
function matchesFilter(
  type: TransactionType,
  filter: TransactionFilter
): boolean {
  if (filter === "all") return true;
  const config = TRANSACTION_CONFIG[type];
  return config.category === filter;
}

/**
 * Format units with appropriate precision
 */
function formatUnits(units: number): string {
  if (units >= 1000) {
    return units.toFixed(2);
  }
  return units.toFixed(3);
}

/**
 * Format NAV with rupee symbol
 */
function formatNav(nav: number): string {
  return `₹${nav.toFixed(4)}`;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Transaction type badge
 */
function TransactionTypeBadge({ type }: { type: TransactionType }) {
  const config = TRANSACTION_CONFIG[type];
  const Icon = config.icon;

  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-medium border-0 gap-1",
        config.bgColor,
        config.textColor
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

/**
 * Filter buttons group
 */
function FilterButtons({
  activeFilter,
  onFilterChange,
}: {
  activeFilter: TransactionFilter;
  onFilterChange: (filter: TransactionFilter) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Filter className="h-4 w-4 text-muted-foreground" />
      <div className="flex gap-1">
        {FILTER_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={activeFilter === option.value ? "default" : "ghost"}
            size="sm"
            onClick={() => onFilterChange(option.value)}
            className={cn(
              "h-7 px-3",
              activeFilter === option.value
                ? ""
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Single transaction row
 */
function TransactionRow({
  transaction,
}: {
  transaction: TransactionDisplayData;
}) {
  const config = TRANSACTION_CONFIG[transaction.type];
  const isSell = config.category === "sell";

  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      {/* Left: Date and Type */}
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {formatDate(transaction.date)}
          </span>
          <TransactionTypeBadge type={transaction.type} />
        </div>
      </div>

      {/* Center: Units and NAV */}
      <div className="flex flex-col items-center text-sm">
        <span className="font-medium">
          {isSell ? "-" : "+"}
          {formatUnits(transaction.units)} units
        </span>
        <span className="text-muted-foreground text-xs">
          @ {formatNav(transaction.nav)}
        </span>
      </div>

      {/* Right: Amount */}
      <div className="text-right">
        <span
          className={cn(
            "text-sm font-semibold",
            isSell ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"
          )}
        >
          {isSell ? "-" : "+"}
          {formatINR(transaction.amount)}
        </span>
      </div>
    </div>
  );
}

/**
 * Empty state when no transactions match filter
 */
function EmptyState({ filter }: { filter: TransactionFilter }) {
  const message =
    filter === "all"
      ? "No transactions found"
      : `No ${filter} transactions found`;

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <Filter className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * Summary stats for filtered transactions
 */
function TransactionSummary({
  transactions,
}: {
  transactions: TransactionDisplayData[];
}) {
  const summary = useMemo(() => {
    let totalBuyAmount = 0;
    let totalSellAmount = 0;
    let buyCount = 0;
    let sellCount = 0;

    for (const tx of transactions) {
      const config = TRANSACTION_CONFIG[tx.type];
      if (config.category === "buy") {
        totalBuyAmount += tx.amount;
        buyCount++;
      } else if (config.category === "sell") {
        totalSellAmount += tx.amount;
        sellCount++;
      }
    }

    return { totalBuyAmount, totalSellAmount, buyCount, sellCount };
  }, [transactions]);

  return (
    <div className="flex gap-4 text-xs text-muted-foreground border-t border-border/50 pt-3 mt-3">
      <div className="flex items-center gap-1">
        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
          {summary.buyCount} buys
        </span>
        <span>({formatINR(summary.totalBuyAmount)})</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-rose-600 dark:text-rose-400 font-medium">
          {summary.sellCount} sells
        </span>
        <span>({formatINR(summary.totalSellAmount)})</span>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Transactions Tab
 *
 * Displays a filterable list of BUY/SELL transactions for a holding.
 *
 * @example
 * ```tsx
 * <TransactionsTab
 *   holdingId="hld_001" as HoldingId
 *   transactions={[
 *     {
 *       id: "txn_001",
 *       date: "2024-01-15",
 *       type: "purchase",
 *       units: 100.5,
 *       nav: 45.2340,
 *       amount: 4546,
 *     },
 *     {
 *       id: "txn_002",
 *       date: "2024-06-01",
 *       type: "redemption",
 *       units: 50.25,
 *       nav: 48.1230,
 *       amount: 2419,
 *     },
 *   ]}
 * />
 * ```
 */
export function TransactionsTab({
  holdingId: _holdingId,
  transactions,
  isLoading = false,
  className,
}: TransactionsTabProps) {
  const [filter, setFilter] = useState<TransactionFilter>("all");

  // Filter and sort transactions (most recent first)
  const filteredTransactions = useMemo(() => {
    const filtered = transactions.filter((tx) => matchesFilter(tx.type, filter));
    return filtered.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [transactions, filter]);

  if (isLoading) {
    return <TransactionsTabSkeleton className={className} />;
  }

  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="pt-4">
        {/* Header with filter */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">
            Transaction History
          </h3>
          <FilterButtons activeFilter={filter} onFilterChange={setFilter} />
        </div>

        {/* Transaction list */}
        {filteredTransactions.length > 0 ? (
          <div>
            {filteredTransactions.map((transaction) => (
              <TransactionRow key={transaction.id} transaction={transaction} />
            ))}
            <TransactionSummary transactions={filteredTransactions} />
          </div>
        ) : (
          <EmptyState filter={filter} />
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for TransactionsTab
 */
export function TransactionsTabSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="pt-4">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-32" />
          <div className="flex gap-1">
            <Skeleton className="h-7 w-12" />
            <Skeleton className="h-7 w-12" />
            <Skeleton className="h-7 w-12" />
          </div>
        </div>

        {/* Transaction rows skeleton */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between py-3 border-b border-border/50 last:border-0"
          >
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}

        {/* Summary skeleton */}
        <div className="flex gap-4 border-t border-border/50 pt-3 mt-3">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

export default TransactionsTab;
